import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowRight, Gift, RefreshCw, Sparkles, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth';
import { api, ApiClientError } from '../api';
import type { DashboardData } from '../types';
import { InstallBanner } from '../components/InstallBanner';
import { Loading } from '../components/Loading';
import { useToast } from '../components/Toast';

function formatNumber(value: number): string {
  return new Intl.NumberFormat('vi-VN').format(value);
}

export function DashboardPage() {
  const { selectedChildId, selectedChild } = useAuth();
  const { show } = useToast();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!selectedChildId) return;
    try {
      const result = await api<DashboardData>(`/dashboard?childId=${encodeURIComponent(selectedChildId)}`);
      setData(result);
    } catch (error) {
      show(error instanceof ApiClientError ? error.message : 'Không thể tải trang chủ.', 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedChildId, show]);

  useEffect(() => {
    setLoading(true);
    void load();
    const refresh = () => void load();
    window.addEventListener('kidee:outbox-flushed', refresh);
    return () => window.removeEventListener('kidee:outbox-flushed', refresh);
  }, [load]);

  const progress = useMemo(() => {
    if (!data) return 0;
    return Math.max(0, Math.min(100, Math.round((data.points.balance / Math.max(1, data.points.target)) * 100)));
  }, [data]);

  if (loading && !data) return <Loading label={`Đang chuẩn bị trang của ${selectedChild?.nickname ?? 'con'}…`} />;
  if (!data) return null;

  return (
    <div className="page dashboard-page">
      <InstallBanner />
      <section className="points-card">
        <div className="points-card__copy">
          <span className="eyebrow">Điểm hiện có</span>
          <div className="points-value"><Star fill="currentColor" /><strong>{formatNumber(data.points.balance)}</strong><span>điểm</span></div>
          <p>Mục tiêu: {formatNumber(data.points.target)} điểm</p>
          <div className="progress-track" aria-label={`Tiến độ ${progress}%`}><div style={{ width: `${progress}%` }} /></div>
          <small>{data.points.balance >= data.points.target ? 'Con đã đạt mục tiêu! 🎉' : `Còn ${formatNumber(data.points.target - data.points.balance)} điểm nữa là đạt mục tiêu.`}</small>
        </div>
        <div className="points-card__mascot" aria-hidden="true">🦕⭐</div>
      </section>

      <section className="surface">
        <div className="section-heading">
          <div><span className="section-icon section-icon--orange"><Sparkles size={19} /></span><h2>Điểm hôm nay</h2></div>
          <Link to="/log">Ghi thêm <ArrowRight size={16} /></Link>
        </div>
        {data.todayEvents.length > 0 ? (
          <div className="activity-list">
            {data.todayEvents.map((event) => (
              <div key={event.id} className="activity-row">
                <span className="activity-icon">{event.delta >= 0 ? '⭐' : '➖'}</span>
                <div><strong>{event.reason}</strong><small>{new Date(event.createdAt).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}</small></div>
                <b className={event.delta >= 0 ? 'positive' : 'negative'}>{event.delta >= 0 ? '+' : ''}{event.delta}</b>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state compact"><span>🌱</span><p>Hôm nay chưa có ghi nhận nào.</p><Link className="button button--soft" to="/log">Ghi điểm đầu tiên</Link></div>
        )}
      </section>

      <section className="surface">
        <div className="section-heading">
          <div><span className="section-icon section-icon--purple"><Gift size={19} /></span><h2>Gợi ý quà cho con</h2></div>
          <Link to="/rewards">Xem tất cả <ArrowRight size={16} /></Link>
        </div>
        {data.suggestions.length > 0 ? (
          <div className="reward-grid reward-grid--compact">
            {data.suggestions.slice(0, 2).map((reward) => (
              <Link to="/rewards" key={reward.id} className="reward-mini-card">
                <div className="reward-visual">{reward.icon}</div>
                <strong>{reward.title}</strong>
                <span>{formatNumber(reward.pointCost)} điểm ⭐</span>
                {reward.score !== undefined && <small>{reward.score}% phù hợp</small>}
              </Link>
            ))}
          </div>
        ) : (
          <div className="empty-state compact">
            <span>🎁</span>
            <p>{data.aiJob?.status === 'pending' || data.aiJob?.status === 'exported' ? 'Kidee đang chờ gợi ý từ AI.' : 'Chưa có gợi ý phù hợp.'}</p>
            <Link className="button button--soft" to="/rewards">Tạo gợi ý</Link>
          </div>
        )}
      </section>

      <section className="budget-strip">
        <div><span>Ngân sách quà còn lại tháng này</span><strong>{formatNumber(data.budget.remainingVnd)}đ</strong></div>
        <div><span>Quà đủ điều kiện</span><strong>{data.eligibleRewardCount}</strong></div>
      </section>

      <button className="refresh-button" type="button" onClick={() => void load()} aria-label="Làm mới dữ liệu">
        <RefreshCw size={16} /> Làm mới
      </button>
    </div>
  );
}
